import * as _ from 'lodash'
import {
    DimensionType,
    IHierarchy,
    IHierarchyFilterOptions,
    IHierarchyLevel,
    IHierarchyNode
} from "../PA.Core/PA.Core.IF.Hierarchy";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {IMessageProvider} from "../PA.Core/PA.Core.IF.Basic";
import { HierarchyUtils } from "../PA.Core/Basic/HierarchyUtils"
import { ExceptionMessage_restate } from "../PA.Core/Basic/MessageUtils"



export interface CreateOptions {}

export class FilteredHierarchy
    implements IHierarchy
{

    private srcHierarchy: IHierarchy
    private filterOptions: IHierarchyFilterOptions
    private _dimensionType: DimensionType
    private _hierarchyId: string
    private _hierarchyUI_Id: string
    private _hierarchyName: string

    private dctHierarchyLevels:   {[LevelId: string]: IHierarchyLevel}
    private dctNodes:             {[NodeId: string]: IHierarchyNode}
    private dctNodesByLevel:      {[LevelId: string]: IHierarchyNode[]}

    private dctNodeParentByChildId:     {[NodeId: string]: IHierarchyNode}
    private dctNodeChildrenByParentId:  {[NodeId: string]: IHierarchyNode[]}
    private arrTopLevelNodes:   IHierarchyNode[]

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete:      Promise<void>
    private _bLoaded:             boolean


    constructor(aHierarchy: IHierarchy, options: IHierarchyFilterOptions) {
        this.srcHierarchy = aHierarchy
        this.filterOptions = options || {}
        this._dimensionType = aHierarchy.DimensionType
        this._hierarchyId = aHierarchy.HierarchyId
        this._hierarchyUI_Id = aHierarchy.HierarchyIdUI
        this._hierarchyName = aHierarchy.HierarchyName
        this._bLoaded = false
        this._loadMessageProvider = new MessageProvider()
        this.dctHierarchyLevels = {}
        this.dctNodes = {}
        this.dctNodesByLevel = {}
        this.dctNodeParentByChildId = {}
        this.dctNodeChildrenByParentId = {}
        this.arrTopLevelNodes = []
    }


    get DimensionType(): DimensionType {
        return this._dimensionType;
    }

    get HierarchyId(): string {
        return this._hierarchyId
    }

    get HierarchyIdUI(): string {
        return this._hierarchyUI_Id
    }

    get HierarchyName(): string {
        return this._hierarchyName
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }


    get OnLoadComplete(): Promise<void> {
        const self = this

        if (self._bLoaded)
            return Promise.resolve()


        if (!_.isEmpty(self._OnLoadComplete))
            return self._OnLoadComplete

        let returnValue = self._OnLoadComplete = (async () => {
            let mp = self._loadMessageProvider
            mp.clearMessages()
            try {
                if (!self.srcHierarchy.bLoaded)
                    await self.srcHierarchy.OnLoadComplete

                self._dimensionType = self.srcHierarchy.DimensionType
                self._hierarchyId = self.srcHierarchy.HierarchyId
                self._hierarchyUI_Id = self.srcHierarchy.HierarchyIdUI
                self._hierarchyName = self.srcHierarchy.HierarchyName

                let arrNodes = self.srcHierarchy.getNodes()
                let dctNodes = _.keyBy(arrNodes, (a) => a.NodeId)

                // Filter Nodes by:
                // 1. Keys
                // 2. Top Level
                // 3. Bottom Level
                //
                if (!_.isEmpty(self.filterOptions.FilterNodeIds)) {
                    dctNodes = HierarchyUtils.pruneHierarchy(dctNodes,
                        self.filterOptions.FilterNodeIds,
                        !self.filterOptions.bExcludeChildren,
                        !self.filterOptions.bExcludeParents)
                }

                let bottomLevelFilter = self.filterOptions.BottomLevel
                if (!_.isEmpty(bottomLevelFilter)) {
                    let bottomNodes = _.filter(_.values(dctNodes), (a) => a.NodeLevel == bottomLevelFilter)
                    let bottomNodeIds = _.map(bottomNodes, (a) => a.NodeId)
                    if (!_.isEmpty(bottomNodeIds))
                        dctNodes = HierarchyUtils.pruneHierarchy(dctNodes,
                            bottomNodeIds, false, true)

                }

                let topLevelFilter = self.filterOptions.TopLevel
                if (!_.isEmpty(topLevelFilter)) {
                    let topNodes = _.filter(_.values(dctNodes), (a) => a.NodeLevel == topLevelFilter)
                    let topNodeIds = _.map(topNodes, (a) => a.NodeId)
                    if (!_.isEmpty(topNodeIds))
                        dctNodes = HierarchyUtils.pruneHierarchy(dctNodes, topNodeIds, true, false)
                }

                arrNodes = _.values(dctNodes)
                self.dctNodes = dctNodes
                self.dctNodesByLevel = _.groupBy(arrNodes, (a) => a.NodeLevel)
                let dctSrcLevels = _.keyBy(self.srcHierarchy.getLevels(), (a) => a.LevelId)
                _.forEach(_.keys(dctSrcLevels), (lvlId) => {
                    if (lvlId in dctSrcLevels) {
                        self.dctHierarchyLevels[lvlId] = dctSrcLevels[lvlId]
                    } else {
                        self.dctHierarchyLevels[lvlId] = {
                            LevelId: lvlId,
                            LevelDepth: 0,
                            LevelName: lvlId
                        }
                    }
                })

                _.forEach(arrNodes, (nd) => {
                    let parentId = nd.ParentId
                    let parent = dctNodes[parentId]

                    if (!_.isEmpty(parent)) {
                        self.dctNodeParentByChildId[nd.NodeId] = parent
                        // Add to Children
                        if (!(parentId in self.dctNodeChildrenByParentId))
                            self.dctNodeChildrenByParentId[parentId] = []

                        self.dctNodeChildrenByParentId[parentId].push(nd)
                    } else {
                        // Add to top Level
                        self.arrTopLevelNodes.push(nd)
                    }
                }) // Next Node

                self._bLoaded = true
            } catch(err) {
                self._bLoaded = false
                ExceptionMessage_restate(mp, err)
            }
        })();


        return returnValue
    }

    getLevels(): IHierarchyLevel[] {
        return _.values(this.dctHierarchyLevels);
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {
        if (NodeId in this.dctNodes) return this.dctNodes[NodeId];

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes)
    }


    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel) return this.dctNodesByLevel[HierarchyLevel];

        return [];
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: IHierarchyNode[] = [];

        const arrRecurseOnChildren: IHierarchyNode[] = [];
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                const arrChildren = this.dctNodeChildrenByParentId[aNodeId];
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel) returnValue.push(aChildNode);
                        else arrRecurseOnChildren.push(aChildNode);
                    } else {
                        returnValue.push(aChildNode);
                    }
                });
            }
        });

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            const moreChildren = this.getChildren(arrRecurseOnChildren, levelId);
            _.forEach(moreChildren, (a) => returnValue.push(a));
        }

        return returnValue;
    }

    getParents(
        nodes: IHierarchyNode[] | IHierarchyNode | string[] | string,
        levelId?: string,
    ): {
        [NodeId: string]: IHierarchyNode;
    } {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: { [NodeId: string]: IHierarchyNode } = {};

        const heightLimit = 20;
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0;
            let parent = this.dctNodeParentByChildId[aNodeId];
            if (bCheckLevel) {
                while (parent && parent?.NodeLevel != levelId && depth < heightLimit) {
                    parent = this.dctNodeParentByChildId[parent.NodeId];
                    depth += 1;
                }
                if (depth == heightLimit) parent = undefined;
            }

            returnValue[aNodeId] = parent;
        });

        return returnValue;
    }

    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        const returnValue: string[] = [];

        if (_.isEmpty(nodes) && !_.isNumber(nodes)) return returnValue;

        let keyList: any[];
        if (_.isArray(nodes)) keyList = nodes;
        else keyList = [nodes];

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem);
                return;
            }

            if (_.isNumber(aKeyItem)) returnValue.push(aKeyItem + "");

            if (_.isEmpty(aKeyItem)) return;

            if ("NodeId" in aKeyItem) returnValue.push(aKeyItem.NodeId);
        });

        return returnValue;
    }


}